import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Battery, Mountain, Atom, Flame, Zap, Sun, Droplets, Factory,
  Fish, Beef, Bird, Milk, PiggyBank, Building2, Trees, Train, Anchor,
  Ship, Container, Snowflake, Code2, Wrench, BriefcaseBusiness, Globe2,
  ArrowRight, BookOpen,
} from "lucide-react";
import { SECTORES, getPaginaPDFCapitulo } from "../lib/sectores";

const ICONS = {
  MapPin, Battery, Mountain, Atom, Flame, Zap, Sun, Droplets, Factory,
  Fish, Beef, Bird, Milk, PiggyBank, Building2, Trees, Train, Anchor,
  Ship, Container, Snowflake, Code2, Wrench, BriefcaseBusiness, Globe2,
};

const HORIZONTE_LABEL = {
  corto: "Corto plazo · 1–3 años",
  mediano: "Mediano plazo · 3–7 años",
  largo: "Largo plazo · 7+ años",
};

const HORIZONTE_COLOR = {
  corto: "#10B981",
  mediano: "#F5B400",
  largo: "#2563EB",
};

export const SectorPorSector = () => {
  const [selectedId, setSelectedId] = useState("turismo");

  const selected = useMemo(
    () => SECTORES.find((s) => s.id === selectedId) || SECTORES[0],
    [selectedId]
  );

  const Icon = ICONS[selected.icon] || MapPin;

  return (
    <section
      id="sector-por-sector"
      className="bg-white py-24 md:py-32"
      data-testid="sector-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#0A2463]" />
              <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#0A2463]">
                {SECTORES.length} sectores · cifras del libro
              </span>
            </div>
            <h2 className="font-headings font-bold uppercase tracking-tight text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Argentina<br /><span className="fy-underline">sector por sector</span>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Elegí una actividad económica y mirá cómo crece según el Capitalismo de Abundancia: diagnóstico, propuesta concreta, cifras del libro e instrumentos que la financian.
            </p>
          </div>
        </div>

        {/* Selector + Grid */}
        <div className="grid lg:grid-cols-12 gap-8 mb-10">
          {/* Select */}
          <div className="lg:col-span-4">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">
              Elegí una actividad
            </label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full bg-white border-2 border-[#0A192F] px-4 py-4 font-headings uppercase tracking-wide text-[#0A192F] text-sm cursor-pointer focus:outline-none focus:border-[#F5B400]"
              data-testid="sector-select"
            >
              {SECTORES.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
            <p className="mt-3 text-xs text-[#9CA3AF]">
              También podés tocar cualquier ícono de la grilla →
            </p>
          </div>

          {/* Grid de íconos */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-7 gap-2" data-testid="sector-grid">
              {SECTORES.map((s) => {
                const SIcon = ICONS[s.icon] || MapPin;
                const active = s.id === selectedId;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    title={s.nombre}
                    className={`group aspect-square flex flex-col items-center justify-center p-2 border-2 transition-all ${
                      active
                        ? "bg-[#0A192F] border-[#0A192F] text-[#F5B400]"
                        : "border-gray-200 text-[#0A192F] hover:border-[#0A192F] hover:bg-gray-50"
                    }`}
                    data-testid={`sector-icon-${s.id}`}
                  >
                    <SIcon size={22} strokeWidth={1.5} />
                    <span className="mt-1 text-[9px] font-headings uppercase tracking-tight leading-tight text-center line-clamp-2">
                      {s.nombre.split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Detail Panel */}
        <AnimatePresence mode="wait">
          <motion.article
            key={selected.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="bg-[#0A192F] text-white relative overflow-hidden"
            data-testid="sector-detail"
          >
            <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 bg-[#F5B400]/10 blur-3xl rounded-full" />
            <div aria-hidden className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#2563EB]/15 blur-3xl rounded-full" />

            <div className="relative p-8 sm:p-12">
              {/* Top row */}
              <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 border-2 border-[#F5B400] flex items-center justify-center">
                    <Icon size={32} className="text-[#F5B400]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-headings text-3xl sm:text-4xl font-bold uppercase tracking-tight leading-tight">
                      {selected.nombre}
                    </h3>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/60 mt-1 font-headings">
                      {selected.capitulo}
                    </p>
                  </div>
                </div>
                <div
                  className="px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-headings"
                  style={{
                    color: HORIZONTE_COLOR[selected.horizonte],
                    border: `1px solid ${HORIZONTE_COLOR[selected.horizonte]}`,
                  }}
                >
                  ● {HORIZONTE_LABEL[selected.horizonte]}
                </div>
              </div>

              {/* Hero metric */}
              <div className="mb-10 pb-8 border-b border-white/10">
                <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2 font-headings">
                  Métrica clave
                </div>
                <div className="font-headings text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F5B400] leading-none tabular-nums">
                  {selected.metricaPrincipal}
                </div>
                <div className="text-white/70 text-sm sm:text-base mt-2">
                  {selected.metricaLabel}
                </div>
              </div>

              {/* Diagnóstico + Propuesta */}
              <div className="grid md:grid-cols-2 gap-10 mb-10">
                <div>
                  <div className="font-headings text-xs tracking-[0.25em] uppercase text-red-300 mb-3">
                    Diagnóstico hoy
                  </div>
                  <p className="text-white/85 leading-relaxed">
                    {selected.diagnostico}
                  </p>
                </div>
                <div>
                  <div className="font-headings text-xs tracking-[0.25em] uppercase text-[#F5B400] mb-3">
                    Propuesta del Capitalismo de Abundancia
                  </div>
                  <p className="text-white/85 leading-relaxed">
                    {selected.propuesta}
                  </p>
                </div>
              </div>

              {/* Cifras */}
              {selected.cifras?.length > 0 && (
                <div className="mb-10">
                  <div className="font-headings text-xs tracking-[0.25em] uppercase text-white/60 mb-4">
                    Cifras del libro
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                    {selected.cifras.map((c) => (
                      <div key={c.label} className="bg-[#0A192F] p-5">
                        <div className="font-headings text-2xl sm:text-3xl font-bold text-white tabular-nums">
                          {c.valor}
                        </div>
                        <div className="text-xs uppercase tracking-wide text-white/55 mt-1">
                          {c.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Instrumentos */}
              <div className="mb-8">
                <div className="font-headings text-xs tracking-[0.25em] uppercase text-white/60 mb-3">
                  Instrumentos vinculados
                </div>
                <div className="flex flex-wrap gap-2">
                  {selected.instrumentos.map((i) => (
                    <span
                      key={i}
                      className="text-xs font-headings uppercase tracking-wide bg-white/10 border border-white/20 text-white px-3 py-1.5"
                    >
                      {i}
                    </span>
                  ))}
                </div>
              </div>

              {/* Proyección */}
              <div className="p-5 border-l-4 border-[#F5B400] bg-[#F5B400]/5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-[#F5B400] mb-2 font-headings">
                  Proyección
                </div>
                <p className="text-white text-base sm:text-lg font-medium leading-relaxed">
                  {selected.proyeccion}
                </p>
              </div>

              {/* Abrir el capítulo en el libro interactivo */}
              <button
                type="button"
                onClick={() => {
                  const page = getPaginaPDFCapitulo(selected.capitulo);
                  window.dispatchEvent(
                    new CustomEvent("openBookReader", { detail: { page } })
                  );
                }}
                className="inline-flex items-center gap-2 text-sm font-headings uppercase tracking-wide text-white/80 hover:text-[#F5B400] transition-colors"
                data-testid="sector-book-link"
              >
                <BookOpen size={16} /> Leer este capítulo en el libro
                <ArrowRight size={14} />
              </button>
            </div>
          </motion.article>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SectorPorSector;
